import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Check, Crown, Users, Star, Loader } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import SubscriptionBuyButton from '../components/SubscriptionBuyButton';

const API = process.env.REACT_APP_BACKEND_URL;

const SubscriptionPage = () => {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [processingTier, setProcessingTier] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchTiers();
    if (user) {
      fetchCurrentSubscription();
    }
  }, [user]);

  const fetchTiers = async () => {
    try {
      const res = await axios.get(`${API}/api/subscriptions/tiers`);
      setTiers(res.data.tiers);
    } catch (error) {
      console.error('Failed to fetch tiers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentSubscription = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const res = await axios.get(`${API}/api/subscriptions/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.active) {
        setCurrentSubscription(res.data.subscription);
      }
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
    }
  };

  const handleSubscribe = async (tier) => {
    if (!user) {
      navigate('/auth?redirect=/subscription');
      return;
    }

    setProcessingTier(tier);

    try {
      const token = localStorage.getItem('access_token');
      const originUrl = window.location.origin;

      const res = await axios.post(
        `${API}/api/subscriptions/create-checkout`,
        { tier, origin_url: originUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Redirect to Stripe checkout
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      console.error('Subscription error:', error);
      alert(error.response?.data?.detail || 'Failed to create subscription');
      setProcessingTier(null);
    }
  };

  const getTierIcon = (tierName) => {
    switch (tierName) {
      case 'supporter':
        return <Users className="w-8 h-8" />;
      case 'family':
        return <Star className="w-8 h-8" />;
      case 'legacy':
        return <Crown className="w-8 h-8" />;
      default:
        return <Star className="w-8 h-8" />;
    }
  };

  const getTierBadgeColor = (tierName) => {
    switch (tierName) {
      case 'supporter':
        return 'bg-blue-500';
      case 'family':
        return 'bg-purple-500';
      case 'legacy':
        return 'bg-[#D4AF37]';
      default:
        return 'bg-gray-500';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center pt-24">
        <Loader className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D0D0D] pt-24 pb-32">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 text-center">
        <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-2">Become a Member</p>
        <h1 className="font-['Bebas_Neue'] text-5xl md:text-6xl">SUBSCRIPTION PLANS</h1>
        <p className="text-[#A3A3A3] mt-4 max-w-2xl mx-auto">
          Join the family and get exclusive discounts on music, books, beats, and more.
        </p>
      </div>

      {/* Current Subscription Banner */}
      {currentSubscription && (
        <div className="max-w-7xl mx-auto px-6 md:px-12 mb-8">
          <div className="bg-[#1A1A1A] border border-[#D4AF37] p-6 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`p-3 ${getTierBadgeColor(currentSubscription.tier)} rounded-full`}>
                  {getTierIcon(currentSubscription.tier)}
                </div>
                <div>
                  <h3 className="font-['Bebas_Neue'] text-2xl">
                    Active: {tiers.find(t => t.tier === currentSubscription.tier)?.display_name}
                  </h3>
                  <p className="text-sm text-[#A3A3A3]">
                    Next billing: {new Date(currentSubscription.current_period_end).toLocaleDateString()}
                  </p>
                  {currentSubscription.discount_code && (
                    <p className="text-sm text-[#D4AF37] mt-2">
                      Your Exclusive Discount Code: <span className="font-bold">{currentSubscription.discount_code}</span>
                      <br />
                      <span className="text-xs text-[#A3A3A3]">Use this code on youngblackamaninc.com for 50% off clothing</span>
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pricing Cards */}
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {tiers.map((tier, index) => {
            const isActive = currentSubscription?.tier === tier.tier;
            const isLegacy = tier.tier === 'legacy';

            return (
              <div
                key={tier.tier}
                className={`relative bg-[#1A1A1A] rounded-lg overflow-hidden transition-all duration-300 hover:scale-105 ${
                  isLegacy ? 'ring-4 ring-[#D4AF37]' : ''
                }`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {isLegacy && (
                  <div className="absolute top-0 left-0 right-0 bg-[#D4AF37] text-black text-center py-2 text-sm font-bold uppercase tracking-wider">
                    ⭐ Best Value ⭐
                  </div>
                )}

                <div className={`p-8 ${isLegacy ? 'pt-16' : ''}`}>
                  {/* Icon */}
                  <div className={`w-16 h-16 ${getTierBadgeColor(tier.tier)} rounded-full flex items-center justify-center mx-auto mb-6`}>
                    {getTierIcon(tier.tier)}
                  </div>

                  {/* Tier Name */}
                  <h3 className="font-['Bebas_Neue'] text-3xl text-center mb-2">{tier.display_name}</h3>

                  {/* Price */}
                  <div className="text-center mb-6">
                    <span className="text-5xl font-bold text-[#D4AF37]">${tier.price}</span>
                    <span className="text-[#A3A3A3]">/{tier.interval}</span>
                  </div>

                  {/* Benefits */}
                  <ul className="space-y-4 mb-8">
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                      <span className="text-sm">
                        <strong>{tier.benefits.music_discount}% off</strong> music downloads
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                      <span className="text-sm">
                        <strong>{tier.benefits.books_discount}% off</strong> books/PDFs
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                      <span className="text-sm">
                        <strong>{tier.benefits.beats_discount}% off</strong> beats licensing
                      </span>
                    </li>
                    {tier.benefits.clothing_discount > 0 && (
                      <li className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                        <span className="text-sm">
                          <strong>{tier.benefits.clothing_discount}% off</strong> clothing
                        </span>
                      </li>
                    )}
                    {(tier.tier === 'family' || tier.tier === 'legacy') && (
                      <li className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                        <span className="text-sm">
                          <strong>AI Read-Aloud</strong> audiobook access on purchased books
                        </span>
                      </li>
                    )}
                    {(tier.tier === 'family' || tier.tier === 'legacy') && (
                      <li className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                        <span className="text-sm">
                          <strong>PDF Download</strong> access on purchased books
                        </span>
                      </li>
                    )}
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                      <span className="text-sm">Unlimited streaming</span>
                    </li>
                    {tier.benefits.early_access && (
                      <li className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                        <span className="text-sm">Early access to new releases</span>
                      </li>
                    )}
                    {tier.benefits.exclusive_content && (
                      <li className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                        <span className="text-sm">Exclusive content access</span>
                      </li>
                    )}
                    {tier.benefits.priority_access && (
                      <li className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                        <span className="text-sm">Priority access to drops</span>
                      </li>
                    )}
                    {isLegacy && (
                      <li className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                        <span className="text-sm">
                          <strong>Unique discount code</strong> for clothing
                        </span>
                      </li>
                    )}
                  </ul>

                  {/* Subscribe Button — Shopify Buy Button when tier is
                      mapped, else legacy Stripe flow. */}
                  {isActive ? (
                    <button
                      disabled
                      className="w-full py-4 rounded font-bold uppercase tracking-wider bg-[#262626] text-[#737373] cursor-not-allowed"
                    >
                      Current Plan
                    </button>
                  ) : tier.shopify_product_id ? (
                    <SubscriptionBuyButton tier={tier} />
                  ) : (
                    <button
                      onClick={() => handleSubscribe(tier.tier)}
                      disabled={processingTier === tier.tier}
                      className={`w-full py-4 rounded font-bold uppercase tracking-wider transition-all ${
                        isLegacy
                          ? 'bg-[#D4AF37] text-black hover:bg-[#C4A137]'
                          : 'bg-white text-black hover:bg-[#E0E0E0]'
                      }`}
                    >
                      {processingTier === tier.tier ? (
                        <Loader className="w-5 h-5 animate-spin mx-auto" />
                      ) : (
                        'Subscribe Now'
                      )}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* FAQ Section */}
      <div className="max-w-4xl mx-auto px-6 md:px-12 mt-20">
        <h2 className="font-['Bebas_Neue'] text-4xl text-center mb-12">Frequently Asked Questions</h2>
        
        <div className="space-y-6">
          <div className="bg-[#1A1A1A] p-6 rounded-lg">
            <h3 className="font-bold text-lg mb-2">Can I cancel anytime?</h3>
            <p className="text-[#A3A3A3] text-sm">
              Yes! You can cancel your subscription at any time. Your access will continue until the end of your billing period.
            </p>
          </div>

          <div className="bg-[#1A1A1A] p-6 rounded-lg">
            <h3 className="font-bold text-lg mb-2">How do discounts work?</h3>
            <p className="text-[#A3A3A3] text-sm">
              Discounts are automatically applied at checkout when you purchase music, books, or beats. For clothing, Legacy members receive a unique discount code to use on youngblackamaninc.com.
            </p>
          </div>

          <div className="bg-[#1A1A1A] p-6 rounded-lg">
            <h3 className="font-bold text-lg mb-2">What's the difference between monthly and yearly?</h3>
            <p className="text-[#A3A3A3] text-sm">
              Supporter and Family plans are monthly ($9.99 and $19.99/month). Legacy is yearly ($99.99/year) and offers the best value with 50% off everything plus exclusive perks.
            </p>
          </div>

          <div className="bg-[#1A1A1A] p-6 rounded-lg">
            <h3 className="font-bold text-lg mb-2">Do I still own the music I buy?</h3>
            <p className="text-[#A3A3A3] text-sm">
              Absolutely! Your subscription gives you discounts on purchases. Once you buy a track or beat, it's yours forever, even if you cancel your subscription.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;
